import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import useIfscLookup from "../../hooks/useIfscLookup";
import { IFSC_LENGTH, isValidIfsc, normalizeIfsc } from "../../utils/ifsc";

/**
 * The line that sits directly under an IFSC input: "KOORIYAD, MALAPPURAM,
 * KERALA" once the code resolves, and the looking-up / not-found / unreachable
 * states on the way there.
 *
 * Separate from IfscField because the two forms that collect an IFSC own their
 * own label-and-input markup — the team wizard's `.form-item` and onboarding's
 * `.ob-field` — and only need to drop this underneath it. The lookup itself
 * lives in useIfscLookup, so mounting this twice for the same code still costs
 * one request.
 *
 * Everything shown here is text written by a third-party service, rendered as
 * a text node: React escapes it, and nothing here builds HTML.
 */
export default function IfscBranchLine({
  id,
  value,
  onResolved,
  touched,
  className,
}) {
  const { status, details, error, retry } = useIfscLookup(value);

  const code = normalizeIfsc(value);
  const valid = isValidIfsc(code);
  const remaining = IFSC_LENGTH - code.length;

  // Two different things, said differently, because conflating them accuses
  // people of mistakes they did not make: SBIN0070610 typed one character
  // short reads as SBIN007061, and calling THAT "not a valid IFSC" tells
  // somebody their correct code is wrong. It isn't wrong, it is unfinished.
  //
  //   unfinished → count what is missing, quietly, and only once they have
  //                left the field (silence while they are still typing)
  //   full and malformed → the code genuinely cannot be one: say so, in red
  const showShortHint = remaining > 0 && code.length > 0 && touched;
  const showFormatError = remaining === 0 && !valid;

  // Hand the resolved branch up once per code, not once per render.
  const deliveredRef = useRef(null);
  useEffect(() => {
    if (status !== "done" || !details) return;
    if (deliveredRef.current === details.ifsc) return;
    deliveredRef.current = details.ifsc;
    onResolved?.(details);
  }, [status, details, onResolved]);

  // The branch, then where it is. Joined from whatever the service actually
  // returned, so a branch with no district still reads properly.
  const place = details
    ? [details.branch, details.district, details.state].filter(Boolean).join(", ")
    : "";

  return (
    <div
      id={id}
      className={`ifsc-status ${className || ""}`.trim()}
      role="status"
      aria-live="polite"
    >
      {showShortHint ? (
        <span className="ifsc-msg is-hint">
          {remaining} more character{remaining === 1 ? "" : "s"} — an IFSC is{" "}
          {IFSC_LENGTH} (you have {code.length}).
        </span>
      ) : showFormatError ? (
        <span className="ifsc-msg is-error">
          Enter a valid IFSC — 4 letters, then 0, then 6 characters.
        </span>
      ) : status === "loading" ? (
        <span className="ifsc-msg is-loading">Looking up…</span>
      ) : status === "done" && details ? (
        <span className="ifsc-msg is-found">
          <b className="ifsc-branch">{place || "Branch not named"}</b>
        </span>
      ) : status === "error" ? (
        <span className="ifsc-msg is-error">
          {error?.kind === "not-found"
            ? "No branch found for this IFSC code."
            : "Couldn't reach the lookup service — check your connection."}
          {error?.kind !== "not-found" && (
            <button type="button" className="ifsc-retry" onClick={retry}>
              Try again
            </button>
          )}
        </span>
      ) : null}
    </div>
  );
}

IfscBranchLine.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  /** Called once per resolved code with
   *  { ifsc, branch, district, state, bank, city }. */
  onResolved: PropTypes.func,
  /** The field has been left and not returned to — lets the format complaint
   *  appear for a code too short to be one. Hosts set it on blur and clear it
   *  on focus, so it never fires mid-typing. */
  touched: PropTypes.bool,
  className: PropTypes.string,
};
